import React from 'react';
import Layout from '../../components/layout/layout';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SectionBackground from '@/components/sections/section-background';
import { WebPageSchema, BreadcrumbSchema, FAQPageSchema } from '../../lib/utils/schema-utils';
import { sanitizeHtml } from '../../lib/sanitizeHtml';

const faqs = [
  {
    title: "En quoi êtes-vous un cabinet de recrutement différent ?",
    content: `<p>Chez Recruteurs.io, nous avons développé une approche unique qui nous distingue des autres cabinets de recrutement. Notre philosophie repose sur trois piliers fondamentaux : l'exigence, l'innovation et la personnalisation.</p>
    <h4>1. <strong>Exigence et Sélectivité</strong></h4><p>Notre processus est ultra rigoureux, avec des évaluations poussées et plusieurs entretiens pour garantir l'adéquation parfaite.</p>
    <h4>2. <strong>Innovation dans le Recrutement</strong></h4><p>Parcours immersifs, mises en situation, retour d'étonnement : notre process est pensé pour révéler les bons profils.</p>
    <h4>3. <strong>Offre Tangible et Mesurable</strong></h4><p>Onboarding inclus, intégration suivie, résultats concrets : on ne se contente pas de placer, on accompagne sur la durée.</p>
    <h4>4. <strong>Personnalisation et Flexibilité</strong></h4><p>Chaque mission est unique : nos services s'adaptent à votre culture, votre marché et vos objectifs.</p>
    <h4>5. <strong>Suivi Post-Recrutement</strong></h4><p>Intégration, feedback, accompagnement : on reste engagés même après la signature.</p>`
  },
  {
    title: "Quels sont vos engagements candidats ?",
    content: `<p>Nous considérons chaque candidat comme un partenaire clé. À Marseille et ailleurs, nos engagements sont simples :</p>
    <h4>1. <strong>Respect et Confidentialité</strong></h4><p>Toutes les candidatures sont traitées de manière confidentielle et professionnelle.</p>
    <h4>2. <strong>Transparence</strong></h4><p>Vous êtes informé à chaque étape du process : pas de zones d'ombre ni de promesses floues.</p>
    <h4>3. <strong>Évaluations Objectives</strong></h4><p>Tests, grilles d'analyse, entretiens structurés… on évalue les compétences, pas les intuitions.</p>
    <h4>4. <strong>Accompagnement</strong></h4><p>On vous aide à vous préparer, à vous positionner, et à réussir vos entretiens.</p>
    <h4>5. <strong>Expérience Positive</strong></h4><p>On fait en sorte que chaque étape soit fluide, utile et enrichissante pour votre parcours.</p>`
  },
  {
    title: "Quelles sont les spécialisations du cabinet ?",
    content: `<p>Nous intervenons dans plusieurs secteurs stratégiques :</p>
    <h4><strong>Cadres et Cadres Supérieurs</strong></h4><p>Nous trouvons des profils de direction capables de structurer et d'accélérer vos projets.</p>
    <h4><strong>Commerce et Vente</strong></h4><p>Représentants, commerciaux, responsables grands comptes : on source les bons moteurs de croissance.</p>
    <h4><strong>Finance et Comptabilité</strong></h4><p>Contrôleurs de gestion, RAF, DAF… des profils solides et opérationnels.</p>
    <h4><strong>RH</strong></h4><p>DRH, RRH, recruteurs, talents managers : nous connaissons les codes de la fonction.</p>
    <h4><strong>Industrie et Ingénierie</strong></h4><p>Techniciens, ingénieurs, chefs de projet industriels : nos méthodes sont adaptées à la complexité de ces métiers.</p>
    <h4><strong>Secteur Public et Social</strong></h4><p>Associations, établissements publics, collectivités : on maîtrise aussi les spécificités de vos environnements.</p>`
  },
  {
    title: "Le cabinet travaille-t-il au succès ?",
    content: `<p>Non. Et on vous explique pourquoi :</p>
    <h4>1. <strong>Engagement Réciproque</strong></h4><p>Un recrutement réussi commence par un vrai engagement mutuel, pas par un pari sans filet.</p>
    <h4>2. <strong>Qualité Supérieure</strong></h4><p>Nos méthodes, nos outils, nos tests nécessitent du temps, de l'expertise… et des moyens. Sans engagement, pas de résultat sérieux.</p>
    <h4>3. <strong>Transparence</strong></h4><p>Vous savez exactement ce que vous payez, et pour quoi. Pas de surprise, pas de frais cachés.</p>
    <h4>4. <strong>Partenariat</strong></h4><p>Nous sommes là pour construire une relation de confiance, pas pour empiler les candidatures.</p>`
  },
  {
    title: "Pouvez-vous me proposer plusieurs postes en même temps ?",
    content: `<p>Non. Pourquoi ? Parce que notre travail, c'est de vous positionner là où vous avez le plus de chances de réussir.</p>
    <h4>1. <strong>Focalisation</strong></h4><p>Un bon recrutement commence par une bonne stratégie. On ne vous envoie pas partout, on vous envoie au bon endroit.</p>
    <h4>2. <strong>Accompagnement</strong></h4><p>Un poste = un accompagnement complet. Sinon, on fait mal les choses.</p>
    <h4>3. <strong>Rigueur</strong></h4><p>Notre approche repose sur la qualité, pas la quantité. On ne « balance » pas des CV.</p>`
  },
  {
    title: "Faites-vous du coaching ?",
    content: `<p>Non, notre cœur de métier, c'est le recrutement. Mais :</p>
    <h4>1. <strong>Onboarding</strong></h4><p>Chaque mission comprend un vrai parcours d'intégration.</p>
    <h4>2. <strong>Conseil RH</strong></h4><p>Processus RH, organisation, conformité… on vous aide à structurer vos fondations.</p>
    <h4>3. <strong>Management de transition</strong></h4><p>Pour accompagner vos phases de croissance ou de transformation.</p>`
  },
];

export default function QuestionsPage() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const schemaFaqs = faqs.map(faq => ({
    question: faq.title,
    answer: faq.content.replace(/<[^>]*>/g, '')
  }));

  return (
    <Layout
      title="FAQ — Vos questions sur le recrutement avec recruteurs.io"
      description="Réponses aux questions fréquentes sur nos missions de recrutement : tarifs, méthode, garantie, délais, périmètre géographique, secteurs et profils traités."
      keywords="faq recrutement, questions cabinet recrutement, tarifs recrutement, garantie recrutement, méthode recrutement"
    >
      <WebPageSchema
        page={{
          title: "FAQ — Vos questions sur le recrutement avec recruteurs.io",
          description: "Réponses aux questions fréquentes sur nos missions de recrutement : tarifs, méthode, garantie, délais, périmètre géographique, secteurs et profils traités.",
          url: "https://recruteurs.io/infos/questions"
        }}
      />
      
      <BreadcrumbSchema
        items={[
          { name: "Accueil", url: "https://recruteurs.io" },
          { name: "Infos", url: "https://recruteurs.io/infos" },
          { name: "Questions fréquentes", url: "https://recruteurs.io/infos/questions" }
        ]}
      />

      <FAQPageSchema questions={schemaFaqs} />

      <SectionBackground sectionId="faq-hero" className="bg-gradient-to-br from-[#ffe0e0] via-[#fef1e4] to-[#e0f0ff] py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-orange-400 to-blue-500">
            Questions fréquentes
          </h1>
          <p className="text-lg text-neutral-700">
            Tout ce que vous devez savoir sur notre cabinet, nos engagements, et notre façon de travailler.
          </p>
        </div>
      </SectionBackground>

      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="border-b border-neutral-300 py-6"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="flex items-center justify-between w-full text-left group"
              >
                <h3 className="text-xl font-semibold text-neutral-900 group-hover:text-blue-600">
                  {faq.title}
                </h3>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-neutral-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-neutral-500" />
                )}
              </button>
              {openIndex === index && (
                <div
                  className="mt-4 text-neutral-700 prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(faq.content) }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </section>
    </Layout>
  );
}